import * as React from "react";
import cn from "classnames";
import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    DatePicker,
    Dialog,
    DialogContent,
    Input,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import ReactDOM from "react-dom";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { FormatOptionLabelMeta, SelectInstance } from "react-select";
import {
    IPaymentBreakdown,
    IUnmatchPaymentResponse,
    PaymentMethod,
    PaymentType,
    WriteOffCodes,
    WriteOffCodesDecription,
    WriteOffSource,
} from "../../types/billing.types";
import UnmatchedPaymentIcon from "../../../../components/Icons/UnmatchPayment";
import ConfirmPaymentIcon from "../../../../components/Icons/ConfirmPayment";
import MarkAsPendingIcon from "../../../../components/Icons/MarkAsPending";
import RefundPaymentIcon from "../../../../components/Icons/RefundPayment";
import {
    formatRemarkOptionLabel,
    paymentDateToDisplay,
} from "../../helpers/billing.helpers";
import {
    addPracticeParamToUrl,
    buildSuccessMessage,
    formatDate,
    handleFormatDatePickerValue,
    handleRemoveEmptyParamFromQueryParams,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import { UserPermisions } from "../../../../utils/types/user";
import SplitPaymentIcon from "../../../../components/Icons/SplitPayment";
import useToast from "../../../../hooks/useToast";
import {
    useUnMatchPayment,
    useUpdatePayment,
} from "../../../../hooks/mutations/billing";
import { IUpdatePayment } from "../../../../utils/types/billing";
import { usePostPayments } from "../../../remits/hooks/remits.mutations";
import { IPaginatedApiResponse } from "../../../../utils/types/api-response";
import SplitPaymentForm from "../SplitPaymentForm/SplitPaymentForm";
import PaymentActionForm from "../PaymentActionForm/PaymentActionForm";
import MinusCircleIcon from "../../../../components/Icons/MinusCircle";
import { useFetchRemitClaims } from "../../../remits/hooks/remits.queries";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import { useGetRemarkCodes } from "../../../remark-codes/hooks/remark-code.queries";
import InfoIcon from "../../../../icons/Info";
import DeleteIcon from "../../../../icons/Delete";
import EditIcon from "../../../../icons/Edit";
import CheckIcon from "../../../../icons/Check";
import TimesIcon from "../../../../icons/Times";
import { useAppSelector } from "../../../../hooks/useRedux";

interface PaymentItemProps {
    item: IPaymentBreakdown;
}

type Dialogs =
    | "unmatch_payment"
    | "move_payment"
    | "refund"
    | "confirm_payment";

type Options = {
    label: React.ReactNode;
    value: string | undefined;
};

export default function PaymentItem({ item }: PaymentItemProps) {
    const getDefaultPaymentSource = () => {
        if (item.payment_type.includes("ci")) return "co_insurance";
        if (item.payment_type.includes("pr100")) return "pr100";
        if (item.payment_type === "ip" || item.payment_type === "rip")
            return "interest_payment";
        return "insurance";
    };

    const [paymentSource, setPaymentSource] = React.useState<string>(() =>
        getDefaultPaymentSource()
    );
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [localDateOfPayment, setLocalDateOfPayment] =
        React.useState<Date | null>(
            item.payment_date
                ? parseISO(
                      item.payment_date.replace("+00:00", "").replace("Z", "")
                  )
                : null
        );
    const [isEditing, setIsEditing] = React.useState(false);
    const codeSelectRefs = React.useRef<(SelectInstance | null)[]>([]);

    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const { practice } = useAppSelector((state) => state.userPractice);
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const changeWriteoffPaymentStatus = useUpdatePayment();
    const updatePayment = useUpdatePayment();
    const postPayments = usePostPayments();
    const unmatchPayment = useUnMatchPayment();
    const clientId = params.clientId as string;
    const noteId = params.noteId as string;

    const remitId = searchParams.get("remit_in_view");
    const isEditingList = searchParams.getAll("editing_ids[]");

    const noteDetails = useFetchSessionNote(
        clientId || "",
        noteId || "",
        Boolean(clientId) && Boolean(noteId)
    );

    const remitClaims = useFetchRemitClaims(
        {
            claim_id: noteDetails?.data?.data.claim_id || "",
        },
        Boolean(noteDetails?.data?.data.claim_id)
    );

    const remarkCode = useGetRemarkCodes();

    const splitPaymentIsMutating = useIsMutating(["split-payment"]);
    const paymentIsMutating = useIsMutating(["add-payment", item.invoice_id]);

    const isRefundPayment =
        item.payment_type === "rci" ||
        item.payment_type === "ri" ||
        item.payment_type === "rpr100" ||
        item.payment_type === "rip";

    const refundPaymentAmountToDisplay = isRefundPayment
        ? `-${showMoneyInAppFormat(item?.amount)}`
        : `${showMoneyInAppFormat(item?.amount)}`;

    const paymentSourcesForSelect: Options[] = Object.values(
        WriteOffSource
    ).map((source) => ({
        value: source,
        label:
            source === WriteOffSource.PR100
                ? "PR100"
                : makeStringFirstLetterCapital(removeEnumUnderscore(source)),
    }));

    const paymentTypeForSelect: Options[] = Object.values(PaymentType).map(
        (type) => ({
            value: type,
            label: makeStringFirstLetterCapital(removeEnumUnderscore(type)),
        })
    );

    const defaultPaymentTypeValue = () => {
        switch (item.payment_type) {
            case "wbi":
            case "wbci":
                return "write_off";

            case "ci":
            case "i":
            case "ip":
            case "pr100":
                return "credit";

            default:
                return "refund";
        }
    };

    const getRemarkCodesToDisplay = () => {
        const newRemarkCodes = item.remark_codes_payment;

        if (newRemarkCodes) return newRemarkCodes;

        if (
            !item.remark_codes ||
            (item.remark_codes && item.remark_codes.length === 0)
        ) {
            return null;
        }

        return Object.fromEntries(item.remark_codes.map((key) => [key, null]));
    };

    const remarkCodes = getRemarkCodesToDisplay();

    const {
        register,
        control,
        reset,
        setValue,
        watch,
        handleSubmit,
        formState: { dirtyFields, errors },
    } = useForm<IUpdatePayment>({
        mode: "onChange",
        resolver: yupResolver(
            yup.object().shape({
                amount: yup.number().min(0),
                payment_date: yup.string(),
                writeoff_code: yup.string(),
                payment_notes: yup.string(),
                remark_codes_payment_form: yup.array().of(
                    yup.object().shape({
                        amount: yup
                            .mixed()
                            .test(
                                "is-amount-valid",
                                "Enter valid amount",
                                (value) => {
                                    if (
                                        value !== undefined &&
                                        value !== null &&
                                        value !== ""
                                    ) {
                                        return yup
                                            .number()
                                            .required("Enter valid amount")
                                            .min(0)
                                            .isValidSync(value);
                                    }

                                    return true;
                                }
                            ),
                        code: yup
                            .string()
                            .when("amount", (amount: string, field) =>
                                amount
                                    ? field.required("Code is required")
                                    : field
                            ),
                    })
                ),
            })
        ),
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "remark_codes_payment_form",
    });

    const paymentType = watch("payment_type") || "";
    const paymentMethod = watch("payment_method");

    const canUnmatchPayments = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_PAYMENTS_WRITE,
        UserPermisions.SUPPORT_PAYMENT_CORRECTION_WRITE,
        UserPermisions.CLIENT_INFO_INVOICE_WRITE,
    ]);

    const handleDialogInView = (dialog: Dialogs | null) => {
        setDialogInView(dialog);
    };

    const getBreakdownPaymentSource = (paymentItemType: string) => {
        if (paymentItemType === "pr100" || paymentItemType === "rpr100") {
            return "PR100";
        }
        if (
            paymentItemType === "i" ||
            paymentItemType === "wbi" ||
            paymentItemType === "ri"
        ) {
            return "Insurance";
        }

        if (paymentItemType === "ip" || paymentItemType === "rip") {
            return "Interest Payment ";
        }

        return "Co-Insurance";
    };

    const getPaymentStatusTag = (status: string) => {
        switch (status) {
            case "completed":
                return (
                    <Badge
                        content={makeStringFirstLetterCapital(status)}
                        type="success"
                    />
                );
            case "failed":
                return (
                    <Badge
                        content={makeStringFirstLetterCapital(status)}
                        type="danger"
                    />
                );
            default:
                return (
                    <Badge
                        content={makeStringFirstLetterCapital(status)}
                        type="warning"
                    />
                );
        }
    };

    const writeOffCodesForSelect: Options[] = Object.values(WriteOffCodes).map(
        (writeoff) => ({
            value: writeoff,
            label: WriteOffCodesDecription[writeoff],
        })
    );

    const claimRemitsForSelect = remitClaims?.data?.data.map((remit) => ({
        value: remit.remittance_id,
        label: `DOS: ${formatDate(remit.date_of_service)} / CN: ${
            remit.claim_number || "--"
        } / PD: ${formatDate(remit.remit_payment_date)}`,
    }));

    const paymentMethodForSelect: Options[] = [
        ...Object.values(PaymentMethod).map((method) => ({
            value: method,
            label:
                method === "cheque"
                    ? "Check"
                    : makeStringFirstLetterCapital(
                          removeEnumUnderscore(method)
                      ),
        })),
    ];

    const getWriteOffCodeDescription = (code: string) => {
        const writeOffDescriptions = Object.values(WriteOffCodesDecription);
        const matchingDescription = writeOffDescriptions?.find(
            (description) =>
                code?.toLowerCase() === description.split(":")[0]?.toLowerCase()
        );
        return matchingDescription || "--";
    };

    const handleCancelEditButtonClick = () => {
        setIsEditing(false);

        searchParams.delete("editing_ids[]", item.invoice_id || "");
        setSearchParams(searchParams);
    };

    const handleEditButtonClicked = () => {
        setValue("amount", item.amount);
        setValue("payment_notes", item.payment_notes || "");
        setValue(
            "remark_codes_payment_form",
            remarkCodes
                ? Object.entries(remarkCodes).map(([code, amount]) => ({
                      code,
                      amount: amount === null ? "" : `${amount}`,
                  }))
                : [{ amount: "", code: "" }]
        );
        setValue("writeoff_code", item.writeoff_code || "");
        setValue("payment_date", item.payment_date || "");
        setValue("payment_type", defaultPaymentTypeValue());
        setValue("remittance_id", item.remittance_id || "");
        setLocalDateOfPayment(
            parseISO(item.payment_date.replace("+00:00", "").replace("Z", ""))
        );
        setIsEditing(() => true);

        if (item.invoice_id && !isEditingList.includes(item.invoice_id)) {
            searchParams.append("editing_ids[]", item.invoice_id);

            setSearchParams(searchParams);
        }
    };

    const paymentTypeToSend = () => {
        let type;
        if (paymentSource === "insurance") {
            switch (paymentType) {
                case "write_off":
                    type = "wbi";
                    break;
                case "refund":
                    type = "ri";
                    break;
                default:
                    type = "i";
            }
        } else if (paymentSource === "co_insurance") {
            switch (paymentType) {
                case "write_off":
                    type = "wbci";
                    break;
                case "refund":
                    type = "rci";
                    break;
                default:
                    type = "ci";
            }
        } else if (paymentSource === "pr100") {
            switch (paymentType) {
                case "refund":
                    type = "rpr100";
                    break;
                default:
                    type = "pr100";
            }
        } else if (paymentSource === "interest_payment") {
            switch (paymentType) {
                case "refund":
                    type = "rip";
                    break;
                default:
                    type = "ip";
            }
        }

        return type;
    };

    const getPaymentTypeTag = (
        type: string,
        method: string,
        info: {
            refund_reason: string;
            cheque_number: string;
            authorization_number: string;
            card_number?: string | null;
        },
        writeoff_code?: string,
        auth_code?: string
    ) => {
        switch (type) {
            case "wbci":
            case "wbi":
                return (
                    <div className="flex items-center gap-x-1 whitespace-nowrap">
                        <Badge
                            type="neutral"
                            content={
                                <>
                                    Write-off:{" "}
                                    <span className="uppercase">
                                        {writeoff_code}
                                    </span>
                                </>
                            }
                        />

                        <ActionButton
                            className="uppercase"
                            variant="normal"
                            size="auto"
                            actionTitle={getWriteOffCodeDescription(
                                writeoff_code || "--"
                            )}
                        >
                            <InfoIcon />
                        </ActionButton>
                    </div>
                );
            case "ri":
            case "rci":
            case "rip":
            case "rpr100":
                return (
                    <div>
                        <Badge
                            type="success"
                            content={
                                <>
                                    {type === "ri" || type === "rip"
                                        ? "Recoupment"
                                        : "Refund"}
                                    :{" "}
                                    {removeEnumUnderscore(
                                        info?.refund_reason
                                    ) ?? "Others"}
                                </>
                            }
                        />
                    </div>
                );
            case "i":
            case "ci":
            case "ip":
            case "pr100":
                return (
                    <div className="flex items-center gap-x-1 whitespace-nowrap">
                        <Badge
                            type="warning"
                            content={
                                <>
                                    Credit:{" "}
                                    {removeEnumUnderscore(
                                        method === "cheque" ? "check" : method
                                    )}
                                    {info?.card_number
                                        ? ` (**** ${info.card_number})`
                                        : ""}
                                </>
                            }
                        />
                        {info?.cheque_number ? (
                            <ActionButton
                                actionTitle={`CHECK NUMBER - ${info.cheque_number}`}
                                className="uppercase"
                                variant="normal"
                                size="auto"
                            >
                                <InfoIcon />
                            </ActionButton>
                        ) : null}
                        {auth_code ? (
                            <ActionButton
                                actionTitle={`AUTHORIZATION CODE - ${auth_code}`}
                                variant="normal"
                                size="auto"
                                className="uppercase"
                            >
                                <InfoIcon />
                            </ActionButton>
                        ) : null}
                    </div>
                );
            case "pi":
                return <Badge type="info" content="Patient insurance" />;

            default:
                return (
                    <div className="flex items-center gap-x-1 whitespace-nowrap">
                        <Badge type="neutral" content={type ?? "others"} />
                        {info ? (
                            <ActionButton
                                actionTitle={info.refund_reason}
                                variant="normal"
                                className="uppercase"
                                size="auto"
                            >
                                <InfoIcon />
                            </ActionButton>
                        ) : null}
                        {info?.authorization_number ? (
                            <ActionButton
                                className="uppercase"
                                variant="normal"
                                size="auto"
                                actionTitle={`AUTHORIZATION CODE - ${info.authorization_number}`}
                            >
                                <InfoIcon />
                            </ActionButton>
                        ) : null}
                    </div>
                );
        }
    };

    const remarkCodeForSelect: Options[] = remarkCode.data
        ? remarkCode.data?.data
              .map((remark) => ({
                  value: remark.code,
                  label: (
                      <span className="capitalize">{`${remark.code}: ${remark.description}`}</span>
                  ),
              }))
              .sort((a, b) => a.value.localeCompare(b.value))
        : [];

    const handleUpdateWriteoffPaymentStatus = (payload: IPaymentBreakdown) => {
        const dataToSend = {
            payment_id: payload.payment_id,
            amount: payload.amount,
            payment_date: handleFormatDatePickerValue(payload.payment_date),
            payment_status: "pending",
        };
        changeWriteoffPaymentStatus.mutate(dataToSend, {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [
                        "get-payment",
                        handleRemoveEmptyParamFromQueryParams({
                            invoice_id: item.invoice_id,
                        }),
                    ],
                });

                toast({
                    mode: "success",
                    message: "Writeoff status updated successfully",
                });
            },

            onError: () => {
                toast({
                    mode: "error",
                    message:
                        "Error updating writeoff status please try again later",
                });
            },
        });
    };

    const handleUpdatePaymentRow = async (payload: IUpdatePayment) => {
        const payloadToSend: IUpdatePayment = {
            amount: item?.payment_type?.includes("wb")
                ? Number(payload.amount)
                : undefined,
            payment_notes: payload.payment_notes,
            payment_date: handleFormatDatePickerValue(payload.payment_date),
            remark_codes: payload.remark_codes,
            writeoff_code: payload.writeoff_code || undefined,
            payment_id: item?.payment_id || "",
            payment_type: paymentTypeToSend(),
            payment_method:
                payload.payment_method || item.payment_method || undefined,
            remittance_id: payload.remittance_id || remitId || undefined,
            claim_id: item.claim_id || undefined,
            payment_info: {
                cheque_number:
                    payload?.payment_info?.cheque_number ||
                    item.payment_info?.cheque_number ||
                    undefined,
            },
        };

        if (dirtyFields.remark_codes_payment_form) {
            const getPairs = payload.remark_codes_payment_form?.map((pair) => ({
                code: pair.code,
                amount: pair.amount ? parseFloat(pair.amount) : null,
            }));

            payloadToSend.remark_codes_payment =
                getPairs && getPairs?.length > 0
                    ? getPairs?.reduce((acc, curr) => {
                          // eslint-disable-next-line no-param-reassign
                          if (curr.code) acc[curr.code] = curr.amount;
                          return acc;
                      }, {} as Record<string, number | null>)
                    : undefined;
        }

        // clear // remark_codes_payment_form used in form
        payloadToSend.remark_codes_payment_form = undefined;

        updatePayment.mutate(payloadToSend, {
            onSuccess: async () => {
                toast({
                    mode: "success",
                    message: "Payment updated successfully.",
                });

                if (item?.payment_status === "pending") {
                    await new Promise<void>((resolve, reject) => {
                        postPayments.mutate(
                            { payment_ids: [item.payment_id] },
                            {
                                onSuccess: () => {
                                    toast({
                                        mode: "success",
                                        message:
                                            "Payment updated and confirmed successfully.",
                                    });
                                    resolve();
                                },
                                onError: (err) => {
                                    toast({
                                        mode: "error",
                                        message:
                                            "Could not confirm payment at this time",
                                    });
                                    reject(err);
                                },
                            }
                        );
                    });
                }
                queryClient.invalidateQueries({
                    queryKey: [
                        "get-payment",
                        handleRemoveEmptyParamFromQueryParams({
                            invoice_id: item.invoice_id,
                        }),
                    ],
                });
                queryClient.invalidateQueries({
                    queryKey: ["invoices"],
                });

                reset();
                setIsEditing(false);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Error updating payment please try again later",
                });
            },
        });
    };

    const handleUnMatchPayment = (paymentId: string) => {
        const invoiceId = item.invoice_id;
        const unMatchPaymentPayload = {
            matched_payments: {
                [invoiceId as string]: [paymentId],
            },
        };
        unmatchPayment.mutate(unMatchPaymentPayload, {
            onSuccess: (response) => {
                queryClient.setQueryData<
                    IPaginatedApiResponse<IUnmatchPaymentResponse>
                >(["get-payment"], (prev) => {
                    const prevRequired =
                        prev as IPaginatedApiResponse<IUnmatchPaymentResponse>;
                    return {
                        ...prevRequired,
                        invoice_status: response.status,
                    };
                });
                queryClient.invalidateQueries({
                    queryKey: [
                        "get-payment",
                        handleRemoveEmptyParamFromQueryParams({
                            invoice_id: item.invoice_id,
                        }),
                    ],
                });
                handleDialogInView(null);
                toast({
                    mode: "success",
                    message: `${buildSuccessMessage(
                        response.data.successful_payments,
                        response.data.failed_payments
                    )}`,
                });
            },
        });
    };

    const handlePostPayment = (id: string) => {
        postPayments.mutate(
            { payment_ids: [id] },
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({
                        queryKey: [
                            "get-payment",
                            handleRemoveEmptyParamFromQueryParams({
                                invoice_id: item.invoice_id,
                            }),
                        ],
                    });
                    handleDialogInView(null);
                    toast({
                        mode: "success",
                        message: res.message || "Payment posted successfully.",
                    });
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not post payment.",
                    });
                },
            }
        );
    };

    return (
        <>
            <AlertDialog open={dialogInView === "unmatch_payment"}>
                <AlertDialogContent
                    isDeleting={!item.transaction_id}
                    isSaveBtnDisabled={unmatchPayment.isLoading}
                    isCancelBtnDisabled={unmatchPayment.isLoading}
                    onCancelClick={() => handleDialogInView(null)}
                    saveText={
                        item.transaction_id
                            ? "Unmatch Payment"
                            : "Delete Payment"
                    }
                    onSaveClick={
                        item.payment_id
                            ? () => handleUnMatchPayment(item.payment_id)
                            : undefined
                    }
                    title={`Are you sure you want to ${
                        item.transaction_id ? "Unmatch" : "Delete"
                    } payment`}
                    description={
                        item.transaction_id ? (
                            <span>
                                Unmatching this payment will remove it from this
                                bill. <br />
                                You will still be able to see this payment in
                                the unmatched payment tab.
                            </span>
                        ) : (
                            <span>
                                Deleting this payment will remove it from your
                                records. <br />
                                You will no longer be able to see this payment.
                            </span>
                        )
                    }
                />
            </AlertDialog>

            {item.amount &&
            item.payment_id &&
            item.invoice_id &&
            item.payment_date ? (
                <Dialog open={dialogInView === "move_payment"}>
                    <DialogContent
                        variant="center"
                        title="Split Payment"
                        handleCloseDialog={() => {
                            handleDialogInView(null);
                            searchParams.delete("is_pr100_split");
                            setSearchParams(searchParams);
                        }}
                        showFooter
                        wrapperClass="w-[800px]"
                        saveText="Split Payment"
                        isSubmitBtnDisabled={splitPaymentIsMutating > 0}
                        isCancelBtnDisabled={splitPaymentIsMutating > 0}
                        submitBtnFormValue="move-payment"
                    >
                        <SplitPaymentForm
                            paymentId={item.payment_id}
                            defaultAmount={item.amount}
                            onFormSubmit={() => handleDialogInView(null)}
                            invoiceToMoveId={item.invoice_id}
                            paymentDate={item.payment_date}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}

            <Dialog open={dialogInView === "refund"}>
                <DialogContent
                    handleCloseDialog={() => handleDialogInView(null)}
                    variant="center"
                    title={
                        paymentSource === "insurance" ||
                        paymentSource === "interest_payment"
                            ? "Add recoupment"
                            : "Add refund"
                    }
                    saveText={
                        paymentSource === "insurance" ||
                        paymentSource === "interest_payment"
                            ? "Add recoupment"
                            : "Add refund"
                    }
                    submitBtnFormValue="action-form"
                    isSubmitBtnDisabled={paymentIsMutating > 0}
                    isCancelBtnDisabled={paymentIsMutating > 0}
                >
                    <PaymentActionForm
                        invoiceId={item.invoice_id}
                        clientId={clientId}
                        onFormSubmit={() => handleDialogInView(null)}
                        transactionId={item.transaction_id || undefined}
                        modalType="refund"
                        paymentId={item.payment_id || ""}
                    />
                </DialogContent>
            </Dialog>

            <div
                className={cn("relative", {
                    "bg-[#FFEDF6]": isRefundPayment,
                })}
                key={item.payment_id}
            >
                <div
                    className={cn(
                        "grid grid-cols-[170px_80px_200px_120px_100px_250px_260px_200px_100px_100px_318px_200px] gap-[--spacing-items] items-center text-base px-[--padding-card-x] py-3 [border-bottom:1px_solid_rgba(0,0,0,0.1)] !text-[#0b132b]",
                        {
                            "grid-cols-[170px_80px_200px_120px_100px_370px_260px_200px_100px_100px_318px_200px]":
                                item.invoice_id &&
                                isEditingList.includes(item.invoice_id),
                        }
                    )}
                >
                    <div className="flex items-center gap-3 max-w-[170px]">
                        {item.payment_type !== "pi" ? (
                            <>
                                {canUnmatchPayments &&
                                item.payment_type !== "pi" ? (
                                    <div>
                                        {item.transaction_id ? (
                                            <ActionButton
                                                size="auto"
                                                variant="normal"
                                                actionTitle="Unmatch Payment"
                                                onClick={() =>
                                                    handleDialogInView(
                                                        "unmatch_payment"
                                                    )
                                                }
                                            >
                                                <UnmatchedPaymentIcon />
                                            </ActionButton>
                                        ) : (
                                            <ActionButton
                                                size="auto"
                                                variant="normal"
                                                onClick={() =>
                                                    handleDialogInView(
                                                        "unmatch_payment"
                                                    )
                                                }
                                                actionTitle="Delete Payment"
                                            >
                                                <DeleteIcon className="stroke-[#0b132b]" />
                                            </ActionButton>
                                        )}
                                    </div>
                                ) : null}

                                {(item.payment_type === "ci" ||
                                    item.payment_type === "pr100") &&
                                item.amount > 0 ? (
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={() => {
                                            handleDialogInView("move_payment");
                                            searchParams.set(
                                                "is_pr100_split",
                                                "true"
                                            );
                                            setSearchParams(searchParams);
                                        }}
                                        actionTitle="Split Payment"
                                    >
                                        <SplitPaymentIcon />
                                    </ActionButton>
                                ) : null}

                                {(item.payment_type === "wbi" ||
                                    item.payment_type === "i") &&
                                item.payment_status === "pending" ? (
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={() => () =>
                                            handlePostPayment(item.payment_id)}
                                        actionTitle="Confirm Payment"
                                    >
                                        <ConfirmPaymentIcon />
                                    </ActionButton>
                                ) : null}
                                {(item.payment_type === "wbi" ||
                                    item.payment_type === "i") &&
                                item.payment_status === "completed" ? (
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={() =>
                                            handleUpdateWriteoffPaymentStatus(
                                                item
                                            )
                                        }
                                        disabled={
                                            changeWriteoffPaymentStatus.isLoading
                                        }
                                        actionTitle="Mark as Pending"
                                    >
                                        <MarkAsPendingIcon />
                                    </ActionButton>
                                ) : null}

                                {item.refundable_amount &&
                                item.refundable_amount > 0 &&
                                (item.payment_channel === "authorize.net" ||
                                    item.payment_channel ===
                                        "authorize_net_patient_portal") ? (
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={() => {
                                            handleDialogInView("refund");
                                        }}
                                        actionTitle={
                                            paymentSource === "insurance" ||
                                            paymentSource === "interest_payment"
                                                ? "Recoupment"
                                                : "Refund"
                                        }
                                    >
                                        <RefundPaymentIcon />
                                    </ActionButton>
                                ) : null}
                                {!isEditing ? (
                                    <ActionButton
                                        size="auto"
                                        variant="normal"
                                        onClick={handleEditButtonClicked}
                                        disabled={isEditing}
                                        actionTitle="Edit payment"
                                    >
                                        <EditIcon className="stroke-[#0b132b]" />
                                    </ActionButton>
                                ) : (
                                    <>
                                        <ActionButton
                                            size="auto"
                                            variant="normal"
                                            onClick={
                                                handleCancelEditButtonClick
                                            }
                                            disabled={updatePayment.isLoading}
                                            actionTitle="Cancel"
                                        >
                                            <TimesIcon className="stroke-danger size-4" />
                                        </ActionButton>

                                        <ActionButton
                                            size="auto"
                                            variant="normal"
                                            onClick={() => {
                                                handleSubmit(
                                                    handleUpdatePaymentRow
                                                )();
                                            }}
                                            disabled={updatePayment.isLoading}
                                            actionTitle="Save"
                                        >
                                            <CheckIcon className="stroke-primary size-4" />
                                        </ActionButton>
                                    </>
                                )}
                            </>
                        ) : null}
                    </div>
                    <div className="w-[80px] font-semibold">
                        {item.remittance_cpt_code || "--"}
                    </div>
                    <div className="flex items-center w-[200px]">
                        {isEditing ? (
                            <div className="flex items-center gap-2">
                                <Controller
                                    name="payment_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            selected={localDateOfPayment}
                                            onChange={(date) => {
                                                field.onChange(date);
                                                setLocalDateOfPayment(date);
                                            }}
                                            className="h-7 w-[120px]"
                                            errorTextClassName="-mt-1 text-[10px]"
                                            popperClassName="!z-[11]"
                                            popperContainer={({ children }) =>
                                                ReactDOM.createPortal(
                                                    children,
                                                    document.body
                                                )
                                            }
                                            popperProps={{
                                                strategy: "fixed",
                                                modifiers: [
                                                    {
                                                        name: "zIndex",
                                                        options: {
                                                            zIndex: 11,
                                                        },
                                                    },
                                                ],
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        ) : (
                            <div className="flex gap-2 items-center">
                                {paymentDateToDisplay(
                                    item.payment_date,
                                    item.payment_channel || ""
                                )}
                            </div>
                        )}
                    </div>

                    <div className="w-100px">
                        {isEditing ? (
                            <Select
                                placeholder="Source"
                                defaultValue={paymentSourcesForSelect.find(
                                    (source) => source.value === paymentSource
                                )}
                                onChange={(val) => {
                                    setPaymentSource(
                                        (val as Options).value as string
                                    );
                                    setValue("payment_type", "credit");
                                }}
                                options={paymentSourcesForSelect}
                                formatOptionLabel={(
                                    selectData: unknown,
                                    formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                                ) => {
                                    return formatOptionLabelMeta.context ===
                                        "menu"
                                        ? (selectData as Options).label
                                        : truncateString(
                                              (selectData as Options)
                                                  .label as string,
                                              12
                                          );
                                }}
                                classNames={{
                                    container: () => "w-[120px]",
                                    control: () => "!h-7 !min-h-7",
                                    menu: () => "!w-[130px] !z-[11]",
                                    option: () => "!text-[11px]",
                                    input: () =>
                                        "!text-caption !h-5 !p-0 !my-0",
                                    menuPortal: () => "!z-[11]",
                                }}
                                isLongListInDialog
                            />
                        ) : (
                            getBreakdownPaymentSource(item.payment_type)
                        )}
                    </div>
                    <span className="w-[100px]">
                        {getPaymentStatusTag(item.payment_status)}
                    </span>
                    <div className="flex gap-x-1 gap-y-2 flex-wrap items-center">
                        {isEditing ? (
                            <>
                                <Controller
                                    name="payment_type"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            isLongListInDialog
                                            placeholder="Payment Type"
                                            options={
                                                paymentSource === "pr100" ||
                                                paymentSource ===
                                                    "interest_payment"
                                                    ? paymentTypeForSelect.filter(
                                                          (type) =>
                                                              type.value !==
                                                              "write_off"
                                                      )
                                                    : paymentTypeForSelect
                                            }
                                            onChange={(val) =>
                                                field.onChange(
                                                    (val as Options).value
                                                )
                                            }
                                            defaultValue={paymentTypeForSelect.find(
                                                (type) =>
                                                    type.value ===
                                                    defaultPaymentTypeValue()
                                            )}
                                            classNames={{
                                                container: () => "w-[120px]",
                                                control: () => "!h-7 !min-h-7",
                                                menu: () => "!w-[120px]",
                                                option: () => "!text-[11px]",
                                                input: () =>
                                                    "!text-caption !h-[20px] !p-0 !my-0",
                                                menuPortal: () => "!z-[11]",
                                            }}
                                        />
                                    )}
                                />
                                {item.payment_type.includes("wb") ||
                                paymentType === "write_off" ? (
                                    <Controller
                                        name="writeoff_code"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                placeholder="Select code"
                                                isLongListInDialog
                                                onChange={(val) =>
                                                    field.onChange(
                                                        (val as Options).value
                                                    )
                                                }
                                                options={writeOffCodesForSelect}
                                                defaultValue={writeOffCodesForSelect.filter(
                                                    (code) =>
                                                        field.value?.includes(
                                                            code.value || ""
                                                        )
                                                )}
                                                isSearchable
                                                formatOptionLabel={(
                                                    selectData,
                                                    meta
                                                ) => {
                                                    return meta.context ===
                                                        "menu"
                                                        ? formatRemarkOptionLabel(
                                                              selectData,
                                                              meta,
                                                              getWriteOffCodeDescription,
                                                              "writeoff_code"
                                                          )
                                                        : truncateString(
                                                              (
                                                                  selectData as Options
                                                              ).label as string,
                                                              10
                                                          );
                                                }}
                                                classNames={{
                                                    container: () =>
                                                        "w-[120px] !z-[12]",
                                                    control: () =>
                                                        "!h-7 !min-h-7",
                                                    menu: () => "!w-[200px]",
                                                    option: () =>
                                                        "!text-[11px]",
                                                    input: () =>
                                                        "!text-caption !h-5 !p-0 !my-0",
                                                    menuPortal: () => "!z-[11]",
                                                }}
                                            />
                                        )}
                                    />
                                ) : null}
                                {paymentType === "credit" ? (
                                    <Controller
                                        name="payment_method"
                                        control={control}
                                        defaultValue={
                                            paymentMethodForSelect.find(
                                                (method) =>
                                                    method.value ===
                                                    item.payment_method
                                            )?.value
                                        }
                                        render={({ field }) => (
                                            <Select
                                                placeholder="Payment method"
                                                options={paymentMethodForSelect}
                                                onChange={(val) => {
                                                    field.onChange(
                                                        (val as Options).value
                                                    );
                                                }}
                                                defaultValue={paymentMethodForSelect.find(
                                                    (method) =>
                                                        method.value ===
                                                        item.payment_method
                                                )}
                                                isLongListInDialog
                                                classNames={{
                                                    container: () =>
                                                        "w-[120px]",
                                                    control: () =>
                                                        "!h-7 !min-h-7",
                                                    menu: () => "!w-[150px]",
                                                    option: () =>
                                                        "!text-[11px]",
                                                    input: () =>
                                                        "!text-caption !h-5 !p-0 !my-0",
                                                    menuPortal: () => "!z-[11]",
                                                }}
                                            />
                                        )}
                                    />
                                ) : null}
                                {(paymentSource === "insurance" ||
                                    paymentSource === "interest_payment") &&
                                !remitId ? (
                                    <Controller
                                        name="remittance_id"
                                        control={control}
                                        defaultValue={
                                            claimRemitsForSelect?.find(
                                                (remit) =>
                                                    remit.value ===
                                                    item.remittance_id
                                            )?.value ||
                                            claimRemitsForSelect?.[0]?.value
                                        }
                                        render={({ field }) => (
                                            <Select
                                                placeholder="Claim remits"
                                                options={claimRemitsForSelect}
                                                defaultValue={
                                                    claimRemitsForSelect?.find(
                                                        (remit) =>
                                                            remit.value ===
                                                            item.remittance_id
                                                    ) ||
                                                    claimRemitsForSelect?.[0]
                                                        ?.value
                                                }
                                                onChange={(val) =>
                                                    field.onChange(
                                                        (val as Options).value
                                                    )
                                                }
                                                isLongListInDialog
                                                className="z-[11]"
                                                classNames={{
                                                    container: () =>
                                                        "w-[245px] !z-[11]",
                                                    control: () =>
                                                        "!h-7 !min-h-7",
                                                    menu: () => "!w-[350px]",
                                                    option: () =>
                                                        "!text-[11px]",
                                                    input: () =>
                                                        "!text-caption !h-5 !p-0 !my-0",
                                                    menuPortal: () => "!z-[11]",
                                                }}
                                                formatOptionLabel={(
                                                    selectData: unknown,
                                                    formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                                                ) => {
                                                    return formatOptionLabelMeta.context ===
                                                        "menu"
                                                        ? (
                                                              selectData as Options
                                                          ).label
                                                        : truncateString(
                                                              (
                                                                  selectData as Options
                                                              ).label as string,
                                                              24
                                                          );
                                                }}
                                            />
                                        )}
                                    />
                                ) : null}
                                {paymentType === "credit" &&
                                paymentMethod === "cheque" ? (
                                    <Input
                                        {...register(
                                            "payment_info.cheque_number"
                                        )}
                                        placeholder="Check number"
                                        className="h-7 w-[120px]"
                                        defaultValue={item.cheque_number || ""}
                                    />
                                ) : null}
                            </>
                        ) : (
                            <>
                                {getPaymentTypeTag(
                                    item.payment_type,
                                    item.payment_method,
                                    item.payment_info,
                                    item.writeoff_code,
                                    item.auth_code
                                )}
                            </>
                        )}
                    </div>
                    <div className="w-[260px]">
                        <div>
                            {isEditing ? (
                                <div>
                                    {fields.map((fieldItem, i) => (
                                        <div
                                            key={fieldItem.id}
                                            className="flex items-start mb-2 gap-x-2"
                                        >
                                            <Controller
                                                name={`remark_codes_payment_form.${i}.code`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        ref={(
                                                            el: SelectInstance | null
                                                        ) => {
                                                            codeSelectRefs.current[
                                                                i
                                                            ] = el;
                                                        }}
                                                        placeholder="Remark code"
                                                        isSearchable
                                                        options={
                                                            remarkCodeForSelect
                                                        }
                                                        onChange={(val) =>
                                                            field.onChange(
                                                                (val as Options)
                                                                    .value
                                                            )
                                                        }
                                                        value={remarkCodeForSelect.find(
                                                            (code) =>
                                                                field.value ===
                                                                (code.value ||
                                                                    "")
                                                        )}
                                                        formatOptionLabel={(
                                                            selectData: unknown,
                                                            formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                                                        ) => {
                                                            return formatOptionLabelMeta.context ===
                                                                "menu"
                                                                ? (
                                                                      selectData as Options
                                                                  ).label
                                                                : (
                                                                      (
                                                                          selectData as Options
                                                                      )
                                                                          .value as string
                                                                  ).toUpperCase();
                                                        }}
                                                        isLongListInDialog
                                                        classNames={{
                                                            menuPortal: () =>
                                                                "!z-[11]",
                                                            container: () =>
                                                                "w-[120px]",
                                                            control: () =>
                                                                "!h-7 !min-h-7",
                                                            menu: () =>
                                                                "!w-[200px]",
                                                            option: () =>
                                                                "!text-[11px]",
                                                            input: () =>
                                                                "!text-caption !h-5 !p-0 !my-0",
                                                        }}
                                                        hasError={
                                                            !!errors
                                                                ?.remark_codes_payment_form?.[
                                                                i
                                                            ]?.code
                                                        }
                                                        errorText={
                                                            errors
                                                                ?.remark_codes_payment_form?.[
                                                                i
                                                            ]?.code?.message
                                                        }
                                                        errorTextClassName="text-[10px] mt-1 block"
                                                    />
                                                )}
                                            />
                                            <div>
                                                <Input
                                                    {...register(
                                                        `remark_codes_payment_form.${i}.amount`
                                                    )}
                                                    placeholder="Amount"
                                                    hasError={
                                                        !!errors
                                                            ?.remark_codes_payment_form?.[
                                                            i
                                                        ]?.amount
                                                    }
                                                    errorText={
                                                        errors
                                                            ?.remark_codes_payment_form?.[
                                                            i
                                                        ]?.amount
                                                            ? "Enter valid amount"
                                                            : ""
                                                    }
                                                    className="!h-7 w-[100px]"
                                                    errorTextClassName="-mt-1 text-[10px]"
                                                    showCurrency
                                                />
                                            </div>
                                            <Button
                                                title="Remove pair"
                                                size="auto"
                                                variant="normal"
                                                className={
                                                    !!errors
                                                        ?.remark_codes_payment_form?.[
                                                        i
                                                    ]?.code ||
                                                    !!errors
                                                        ?.remark_codes_payment_form?.[
                                                        i
                                                    ]?.amount
                                                        ? "self-start"
                                                        : "self-center"
                                                }
                                                onClick={() => {
                                                    remove(i);
                                                    const focusIndex =
                                                        i > 0 ? i - 1 : 0;
                                                    setTimeout(() => {
                                                        codeSelectRefs.current[
                                                            focusIndex
                                                        ]?.focus();
                                                    }, 0);
                                                }}
                                                disabled={fields.length === 1}
                                            >
                                                <MinusCircleIcon />
                                            </Button>
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-center">
                                        <Button
                                            size="auto"
                                            variant="normal"
                                            className="text-caption underline font-medium leading-none"
                                            onClick={() => {
                                                append(
                                                    { amount: "", code: "" },
                                                    {
                                                        shouldFocus: false,
                                                    }
                                                );
                                                setTimeout(() => {
                                                    codeSelectRefs.current[
                                                        fields.length
                                                    ]?.focus();
                                                }, 0);
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex gap-2 flex-wrap">
                                    {remarkCodes
                                        ? Object.entries(remarkCodes).map(
                                              ([code, amount]) => (
                                                  <div
                                                      key={code}
                                                      className="flex items-center gap-x-1"
                                                  >
                                                      <Badge
                                                          bgColor="#BAECD1"
                                                          textColor="#44474F"
                                                          type="custom"
                                                          content={code}
                                                      />

                                                      <ActionButton
                                                          variant="normal"
                                                          size="auto"
                                                          actionTitle={
                                                              amount
                                                                  ? showMoneyInAppFormat(
                                                                        amount
                                                                    )
                                                                  : "--"
                                                          }
                                                      >
                                                          <InfoIcon />
                                                      </ActionButton>
                                                  </div>
                                              )
                                          )
                                        : "--"}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap w-[318px]">
                        {isEditing && item.payment_type.includes("wb") ? (
                            <Input
                                {...register("amount")}
                                className="h-7 w-[100px]"
                                showCurrency
                            />
                        ) : (
                            <div className="flex gap-2 items-center">
                                {(item.split_group_id ||
                                    item.parent_payment_id) &&
                                item.payment_type !== "rci" ? (
                                    <ActionButton
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            columnGap: "4px",
                                        }}
                                        className="flex items-center gap-x-[4px]"
                                        actionTitle="Split Payment"
                                    >
                                        {refundPaymentAmountToDisplay}
                                        <InfoIcon />
                                    </ActionButton>
                                ) : (
                                    <span>{refundPaymentAmountToDisplay}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <span className="overflow-x-auto w-[100px]">
                        {item.eft_number ? (
                            <Link
                                to={addPracticeParamToUrl(
                                    `/remit/EOB/${item.remittance_id}`,
                                    practice?.practice_id
                                )}
                                target="_blank"
                                rel="noreferrer"
                                className="text-textLink text-sm underline"
                            >
                                {item.eft_number}
                            </Link>
                        ) : (
                            "--"
                        )}
                    </span>
                    <span className="w-[100px]">
                        {Object.keys(item.posted_by).length > 0
                            ? `${item.posted_by.first_name} ${item.posted_by.last_name}`
                            : "Mantle"}
                    </span>
                    <div className="flex flex-wrap w-[318px]">
                        {isEditing ? (
                            <TextArea
                                {...register("payment_notes")}
                                className="h-7 w-[200px] resize-y py-0"
                                placeholder="Notes"
                            />
                        ) : (
                            item.payment_notes || "--"
                        )}
                    </div>
                    <span className="flex flex-wrap w-[200px]">
                        {item.payment_description || "--"}
                    </span>
                </div>
            </div>
        </>
    );
}
